import { Card, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { RangeWarnings } from '../components/RangeWarnings';
import { RejectedActionsAlert } from '../components/RejectedActions';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useBusinessDate } from '../hooks/useSeedLocation';
import { formatMoney } from '../lib/format';

interface ReferralBalance {
  profileId: string;
  accruedMinor: number;
  currency: string;
  payable: boolean;
}

interface Payable {
  invoice: { totalMinor: number; paidMinor: number; currency: string };
  agingDays: number;
  /** Billed, less paid, LESS CREDITED — the server's figure, and the only one this board may show. */
  outstandingMinor: number;
}

interface Device {
  status?: string;
  pendingMutations?: number;
  pendingMedia?: number;
}

interface TipsReport {
  /** Every tip taken on a card and never handed over, from the very first day. */
  unpaidLiability: { currency: string; totalMinor: number }[];
}

/**
 * Lempiras and dollars are separate debts, so a single total across both would be true of
 * neither. Every money tile shows one line per currency instead.
 */
function totalsByCurrency(amounts: { minor: number; currency: string }[]): string[] {
  const totals = new Map<string, number>();
  for (const a of amounts) totals.set(a.currency, (totals.get(a.currency) ?? 0) + a.minor);
  return [...totals.entries()]
    .filter(([, minor]) => minor !== 0)
    .map(([currency, minor]) => formatMoney(minor, currency));
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

interface TileProps {
  label: string;
  /** Where the full detail lives — every tile is a way in, not a dead end. */
  to: string;
  query: UseQueryResult<unknown>;
  /** The headline figures: one line per currency, or a single count. */
  values: string[];
  hint?: string;
  /** Said when there is genuinely nothing outstanding — good news, not an error. */
  empty: string;
}

function Tile({ label, to, query, values, hint, empty }: TileProps) {
  return (
    <Card withBorder radius="md" padding="md" component={Link} to={to}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      {query.isLoading ? (
        <Loader size="sm" mt="sm" />
      ) : query.isError ? (
        <Text size="sm" c="dimmed" mt="xs">
          Could not be loaded just now. Open the page to try again.
        </Text>
      ) : values.length === 0 ? (
        <Text size="sm" c="dimmed" mt="xs">
          {empty}
        </Text>
      ) : (
        <Stack gap={2} mt={6}>
          {values.map((v) => (
            <Text key={v} fz={26} fw={700} lh={1.15}>
              {v}
            </Text>
          ))}
        </Stack>
      )}
      {hint && !query.isError && !query.isLoading && (
        <Text size="sm" c="dimmed" mt="xs">
          {hint}
        </Text>
      )}
    </Card>
  );
}

export function DashboardPage() {
  const devices = useQuery({
    queryKey: listKey('/admin/v1/operations/devices'),
    queryFn: () => rawApi<Device[]>('GET', '/admin/v1/operations/devices'),
  });
  const balances = useQuery({
    queryKey: listKey('/admin/v1/referrals/balances'),
    queryFn: () => rawApi<ReferralBalance[]>('GET', '/admin/v1/referrals/balances'),
  });
  const payables = useQuery({
    queryKey: listKey('/admin/v1/suppliers/payables'),
    queryFn: () => rawApi<Payable[]>('GET', '/admin/v1/suppliers/payables'),
  });
  // Card tips are collected by the business and handed over later, so until they are paid out they
  // are a debt to the staff exactly like a supplier bill — and it was the one debt this board did
  // not show. The running balance ignores the dates, so today's are sent and the reply is all-time.
  //
  // The DAY IS THE BAR'S, not the browser's. This read was `toIsoDate(new Date())`: the viewer's
  // calendar, rolling at midnight. The report ranges on `business_days.business_date`, which rolls
  // at 04:00 on the island — so between midnight and four, with service still running, "today"
  // named a night the bar had not started trading and the tile read nothing owed. For anyone
  // reading from another country it was wrong at other hours too. Undefined until the location has
  // loaded, which is not the same as today and must not be sent as one.
  //
  // AND IT IS A REPORT, which is the owner's as of 2026-08-10. The gate is the mount, so this tile
  // would answer 403 for a manager and render as a broken figure on the first screen they see —
  // so it is not asked for at all, and the tile is absent rather than empty. Every other tile on
  // this board reads a surface a manager holds.
  const todayIso = useBusinessDate();
  const { isOwner } = useCurrentUser();
  const tips = useQuery({
    queryKey: ['dashboard-tips-owed', todayIso],
    queryFn: () => rawApi<TipsReport>('GET', `/admin/v1/reports/tips?from=${todayIso}&to=${todayIso}`),
    enabled: Boolean(todayIso) && isOwner,
  });

  const deviceRows = devices.data ?? [];
  const awaiting = deviceRows.filter((d) => d.status === 'pending').length;
  // A register holding sales the office has never received is money that is not in any report yet.
  const behind = deviceRows.filter(
    (d) => d.status !== 'revoked' && ((d.pendingMutations ?? 0) > 0 || (d.pendingMedia ?? 0) > 0),
  ).length;

  const balanceRows = balances.data ?? [];
  // What is owed is listed per currency, so somebody owed both lempiras and dollars is two rows.
  // The tile counts people — "3 partners" has to mean three people standing at the bar.
  const partnersOwed = new Set(balanceRows.map((b) => b.profileId)).size;
  const readyToPay = new Set(balanceRows.filter((b) => b.payable).map((b) => b.profileId)).size;

  const payableRows = payables.data ?? [];
  // What is still owed on a bill, not what it was worth when it arrived — and not
  // `totalMinor − paidMinor` either, which is that same sentence written wrong: it is over by the
  // whole of anything the vendor has credited back, so a bill credited to nothing was counted here
  // at its full face value on the tile the owner opens first.
  const outstanding = payableRows.map((p) => ({
    minor: p.outstandingMinor,
    currency: p.invoice.currency,
  }));
  const oldest = payableRows.reduce((max, p) => Math.max(max, p.agingDays), 0);

  const tipsOwed = tips.data?.unpaidLiability ?? [];

  return (
    <>
      <Title order={3} mb={4}>
        Dashboard
      </Title>
      <Text c="dimmed" size="sm" mb="md">
        What is owed and what needs a decision. Open any card for the detail behind it.
      </Text>
      {/* A CAI range running out stops the registers printing invoices mid-service, and a replacement
          takes days to obtain — so it cannot live only on a screen nobody opens without a reason.
          It is the one thing here that stops the business trading, so it goes above the money. */}
      <RangeWarnings />
      {/* A comp or a payment a register could not get through is money that is not on any bill, and the
          only person who can put it right is standing in this office. It has to be said somewhere
          the owner already looks — nobody opens the device screen on an ordinary Tuesday. */}
      <RejectedActionsAlert />
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        <Tile
          label="Commission owed"
          // At the tab holding this figure, not at the screen that contains it. The partners list
          // opens first and says nothing about money, so the tile used to hand the reader a number
          // and then a page they had to find it on again.
          to="/referrals?tab=balances"
          query={balances}
          values={totalsByCurrency(balanceRows.map((b) => ({ minor: b.accruedMinor, currency: b.currency })))}
          empty="Nothing owed to referral partners."
          hint={
            balanceRows.length
              ? `${plural(partnersOwed, 'partner', 'partners')}, ${readyToPay} ready to pay`
              : undefined
          }
        />
        <Tile
          label="Bills to pay"
          // Same reason as the tile above: this one opened the vendor list, which is a page about
          // who you buy from, not about what is owed.
          to="/suppliers?tab=payables"
          query={payables}
          values={totalsByCurrency(outstanding)}
          empty="No supplier bills outstanding."
          hint={
            payableRows.length
              ? `${plural(payableRows.length, 'bill', 'bills')}${oldest > 0 ? `, oldest ${plural(oldest, 'day', 'days')} past due` : ''}`
              : undefined
          }
        />
        {isOwner && (
          <Tile
            label="Tips owed to staff"
            to="/reports"
            query={tips}
            values={totalsByCurrency(tipsOwed.map((t) => ({ minor: t.totalMinor, currency: t.currency })))}
            empty="Everyone has been paid their tips."
            hint={tipsOwed.length ? 'Card and transfer tips not yet handed over' : undefined}
          />
        )}
        {/* The headline used to be how many devices exist, which is a number that never changes
            and asks nothing of anyone. This card sits on a board about what needs a decision, so
            it shows the count that needs one — and says so plainly when nothing does. */}
        <Tile
          label="Devices"
          to="/devices"
          query={devices}
          values={awaiting > 0 ? [String(awaiting)] : behind > 0 ? [String(behind)] : []}
          empty={
            deviceRows.length
              ? 'Every register approved and up to date.'
              : 'No registers set up yet — add one.'
          }
          hint={
            awaiting > 0
              ? `${plural(awaiting, 'device', 'devices')} waiting for you to approve`
              : behind > 0
                ? `${plural(behind, 'register', 'registers')} holding sales the office has not received`
                : undefined
          }
        />
      </SimpleGrid>
    </>
  );
}
